import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save } from "lucide-react";

interface Step3SkillsProps {
  skills: string[];
  selectedSkills: string[];
  onSelectionChange: (selected: string[]) => void;
  onSave: () => void;
  onBack: () => void;
  isSaving?: boolean;
}

export const Step3Skills: React.FC<Step3SkillsProps> = ({
  skills,
  selectedSkills,
  onSelectionChange,
  onSave,
  onBack,
  isSaving = false
}) => {
  const handleToggleSkill = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      onSelectionChange(selectedSkills.filter(s => s !== skill));
    } else {
      onSelectionChange([...selectedSkills, skill]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(skills);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            Advanced Tailoring - Step 3 of 3
          </CardTitle>
          <Badge variant="secondary" className="px-3 py-1">
            Skills & Keywords
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            Select the skills and keywords that match your expertise
          </p>
          <div className="text-sm text-muted-foreground">
            {selectedSkills.length} of {skills.length} selected
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Selection Controls */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSelectAll}
          >
            Select All
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDeselectAll}
          >
            Deselect All
          </Button>
        </div>

        {/* Skills List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {skills.map((skill, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => handleToggleSkill(skill)}
            >
              <Checkbox
                checked={selectedSkills.includes(skill)}
                onChange={() => handleToggleSkill(skill)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-primary">
                    #{index + 1}
                  </span>
                  <Badge 
                    variant={index < 7 ? "default" : index < 14 ? "secondary" : "outline"}
                    className="text-xs"
                  >
                    {index < 7 ? "Core Skill" : index < 14 ? "Valuable" : "Beneficial"}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed">{skill}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Requirements
          </Button>
          <Button 
            onClick={onSave}
            disabled={selectedSkills.length === 0 || isSaving}
          >
            {isSaving ? "Saving..." : "Save & Continue"}
            <Save className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};